/**
 * @fileoverview Publishing a collection, and the one mistake that would revoke
 * everybody without telling anyone.
 *
 * Inherits the page publisher's disciplines rather than re-deriving them,
 * because a collection is an addressable event republished over its own previous
 * version and has every hole a page has:
 *
 *   - the size is measured against the TARGET relay's advertised limit and
 *     refused locally, before the signer is asked, because an oversized message
 *     closes the socket with no `OK` and no `NOTICE` and the author would
 *     otherwise watch it fail forever with no idea why;
 *   - the timestamp is kept strictly increasing PER ADDRESS, because two writes
 *     inside one second are both accepted with a positive acknowledgement and
 *     the relay then keeps the one with the lower event id. That is a false
 *     success: the author is told it worked and the newer version is gone.
 *
 * ---
 *
 * THE MISTAKE THAT IS SPECIFIC TO COLLECTIONS: MINTING A NEW KEY ON EVERY WRITE.
 *
 * A collection is rewritten constantly. Every add, every removal, every reorder
 * is a new version of one event. The obvious publisher generates a collection
 * key, seals the members under it, and publishes -- and does that every time.
 *
 * That silently revokes every recipient. Their envelope wraps the OLD key, the
 * sealed half is now under a new one, and nothing tells them or the author. The
 * collection still renders for the owner, who has a fresh owner envelope, so the
 * break is invisible from the only screen anybody is looking at.
 *
 * So the key is an INPUT to an edit, never something this function invents. It
 * is minted exactly once, when a collection is first created, and after that it
 * is recovered from the owner's envelope on the existing event and reused. A
 * caller editing a collection without its key is refused rather than served,
 * because the alternative is a save that looks fine and cuts everybody off.
 *
 * ROTATING THE COLLECTION KEY IS A SEPARATE, DELIBERATE ACT -- removing someone
 * from the whole site -- and it re-issues every recipient's envelope. It is not
 * something an edit does by accident.
 *
 * ---
 *
 * THE OWNER'S ENVELOPE IS MANDATORY ON EVERY COLLECTION, not just private ones,
 * and that is a consequence of sealing every collection rather than only the
 * ones with something to hide. The sealed half is the authoritative member list
 * for EVERY collection now, so without the owner's envelope an author cannot
 * open their own site. This function will not write a collection without one.
 */

import { type ContentKey, type SignerInterface, generateContentKey } from '@cloistr/auth/core';
import type { Event, EventTemplate, UnsignedEvent } from 'nostr-tools';


import { type Collection, buildCollectionTags } from './collectionEvent';
import { type CollectionMember, ownerKeyTag } from './collectionKeys';
import { PAGE_COLLECTION_KIND } from './kinds';
import { checkName } from './nameCheck';
import { measureEventFrame, nextCreatedAt } from './publishPage';
import { isValidIdentifier } from './slug';

import { encodeAddress } from '@/services/nostr/pageLinks';
import { fetchRelayLimits } from '@/services/nostr/relayInfo';
import { publishEvent } from '@/services/nostr/relays';


export interface CollectionAddress {
  kind: number;
  pubkey: string;
  identifier: string;
}

export type CollectionPublishOutcome =
  | {
      status: 'published';
      address: CollectionAddress;
      naddr: string;
      acceptedBy: string[];
      /**
       * The key this collection is sealed under, handed back so the CALLER can
       * hold it.
       *
       * RETURNED ON EVERY PUBLISH, INCLUDING AN EDIT, and returned at all
       * because of a trap that only appears one save later. On a create this
       * function mints the key; if it kept it, the caller would have no key, and
       * the very next save would be an edit arriving without one and refused --
       * correctly, and infuriatingly, one second after the collection was made.
       * The author would be locked out of the thing they had just created, and
       * the only way back would be re-reading the event and unwrapping the owner
       * envelope, which is a relay round trip and a signer prompt to recover
       * something we had in our hand.
       *
       * The page publisher already returns a new page's key for exactly this
       * reason. This is that rule, one level up.
       */
      key: ContentKey;
    }
  /** Refused locally, before sending, so the author keeps their work. */
  | { status: 'too-large'; bytes: number; limit: number; advertised: boolean; relay: string }
  /** A name that could not be part of an address. */
  | { status: 'invalid-name'; identifier: string }
  /**
   * An edit arrived without the collection's existing key.
   *
   * Refused rather than served with a fresh one, because publishing under a new
   * key would revoke every recipient with nothing to show for it on any screen.
   */
  | { status: 'key-required' }
  /**
   * A NEW site at a name this author already holds. Publishing it would replace
   * the existing site's member list AND seal it under a fresh key, cutting off
   * every recipient. Refused; editing the existing site is the way to change it.
   */
  | { status: 'name-taken'; identifier: string }
  /** Not known to be free, because these relays never answered. Refused. */
  | { status: 'name-unchecked'; identifier: string; relays: string[] }
  /** Every relay refused it, in their own words. */
  | { status: 'rejected'; reasons: Record<string, string> }
  | { status: 'signer-failed'; message: string };

export interface CollectionDraft extends Collection {
  /**
   * The collection's key.
   *
   * REQUIRED FOR AN EDIT and omitted only when creating. See the file comment:
   * a publisher that invents one on every write revokes everybody silently.
   */
  key?: ContentKey;
  /** True when this collection has never been published. */
  creating?: boolean;
  /**
   * Each member's page key, to be sealed into this collection's encrypted half.
   *
   * THIS IS WHAT MAKES SHARING A SITE MEAN ANYTHING. Without it a recipient gets
   * the collection key, sees every private member's title in its real position,
   * and cannot open one of them; and the author cannot see that, because they
   * open every page from its own owner envelope.
   *
   * CARRIED BY THE CALLER RATHER THAN FETCHED HERE, deliberately. Recovering a
   * page's key means reading that page's event and unwrapping its owner
   * envelope, which is a relay round trip and a signer prompt per member. Doing
   * it here would charge a forty-page site forty prompts on every save. The
   * editor pays it once, when a member is added, and carries the keys through
   * every later edit inside the sealed half it already opens.
   */
  memberKeys?: readonly CollectionMember[];
}

/**
 * Write a collection to the relays.
 *
 * ORDER OF REFUSALS IS DELIBERATE, cheapest and least destructive first: a name
 * that cannot be an address, then a missing key, then the size. All three happen
 * BEFORE the signer is asked, because asking someone to approve an event we have
 * already decided to throw away costs them a prompt and, on a remote signer, a
 * notification on their phone.
 */
export async function publishCollection(
  draft: CollectionDraft,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<CollectionPublishOutcome> {
  const identifier = draft.identifier.trim().toLowerCase();
  if (!isValidIdentifier(identifier)) {
    return { status: 'invalid-name', identifier: draft.identifier };
  }

  // An edit without the key would publish under a new one and cut off every
  // recipient. Refused before anything else happens.
  if (!draft.creating && !draft.key) return { status: 'key-required' };
  const key = draft.key ?? generateContentKey();

  let pubkey: string;
  try {
    pubkey = await signer.getPublicKey();
  } catch (e) {
    return { status: 'signer-failed', message: signerMessage(e) };
  }

  const coordinate = `${PAGE_COLLECTION_KIND}:${pubkey}:${identifier}`;

  // A NEW SITE MUST NOT LAND ON AN EXISTING ONE. Before the owner envelope,
  // which is a signer prompt, and before anything is sent.
  if (draft.creating) {
    const name = await checkName([PAGE_COLLECTION_KIND], pubkey, identifier, [...relays]);
    if (name.status === 'taken') return { status: 'name-taken', identifier };
    if (name.status === 'unchecked') {
      return { status: 'name-unchecked', identifier, relays: name.relays };
    }
  }

  // The owner's envelope. Mandatory on EVERY collection, see the file comment.
  let owner: string[];
  try {
    owner = await ownerKeyTag(key, coordinate, signer);
  } catch (e) {
    return { status: 'signer-failed', message: signerMessage(e) };
  }

  const { tags, content } = buildCollectionTags(
    { ...draft, identifier },
    {
      collectionKey: key,
      ownerKeyTag: owner,
      ...(draft.memberKeys ? { memberKeys: draft.memberKeys } : {}),
    }
  );

  const unsigned: UnsignedEvent = {
    kind: PAGE_COLLECTION_KIND,
    pubkey,
    created_at: nextCreatedAt(coordinate),
    content,
    tags,
  };

  const target = relays[0];
  if (!target) return { status: 'rejected', reasons: {} };

  const bytes = measureEventFrame(unsigned);
  const limits = await fetchRelayLimits(target);
  if (bytes > limits.maxMessageBytes) {
    return {
      status: 'too-large',
      bytes,
      limit: limits.maxMessageBytes,
      advertised: limits.advertised,
      relay: target,
    };
  }

  let signed: Event;
  try {
    signed = await signer.signEvent(unsigned);
  } catch (e) {
    return { status: 'signer-failed', message: signerMessage(e) };
  }

  const { accepted, refused } = await publishEvent([...relays], signed, (template: EventTemplate) =>
    signer.signEvent({ ...template, pubkey })
  );

  if (accepted.length === 0) {
    const reasons: Record<string, string> = {};
    for (const r of refused) reasons[r.relay] = r.reason;
    return { status: 'rejected', reasons };
  }

  const address = { kind: PAGE_COLLECTION_KIND, pubkey, identifier };
  return {
    status: 'published',
    address,
    naddr: encodeAddress({ ...address, relays: accepted }),
    acceptedBy: accepted,
    key,
  };
}

/**
 * Repeat the signer's own words rather than flattening them.
 *
 * A remote signer's refusal is often the only thing that tells an author their
 * phone is asleep or their approval expired, and "publishing failed" tells them
 * nothing they can act on.
 */
function signerMessage(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  return 'The signer refused without saying why.';
}
